/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import { bytesToUnitString } from '../../../../utils/DataAmountUtils';
import { bpsToUnitString } from '../../../../utils/DataRateUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER, formatSlaForDisplay } from '../../../../utils/StringUtils';
import { ChartItemKeys, chartSeriesContent } from './ChartSeriesItems';
import { SECONDARY_HISTORY_SUFFIX } from '../../FullTailView/charts/ChartWrappers/WithTerminalHistoryChartHOC';
import TerminalHistory from '../../../../shared/entities/terminalHistory';

/**
 * Get the Highcharts tooltip point formatter function that wraps the bytesToUnitString function for each value's
 * tooltip. This gives the ability to return a formatted value rather than the raw Bytes value returned from the backend.
 */
export const getTooltipPointFormatterWithBytes: Highcharts.FormatterCallbackFunction<Highcharts.Point> = function () {
  return !isNil(this.y) ? bytesToUnitString(this.y) : '';
};

/**
 * Get the Highcharts tooltip point formatter function that wraps the bpsToUnitString function for each value's
 * tooltip. This gives the ability to return a formatted value rather than the raw bps value returned from the backend.
 */
export const getTooltipPointFormatterWithBps: Highcharts.FormatterCallbackFunction<Highcharts.Point> = function () {
  return !isNil(this.y) ? bpsToUnitString(this.y) : '';
};
/**
 * Get the Highcharts tooltip point formatter function that truncates more than two decimals from the value displays on
 * the tooltip.
 */
export const getTooltipPointFormatterToFixedTwoDigits: Highcharts.FormatterCallbackFunction<Highcharts.Point> =
  function () {
    return !isNil(this.y) ? `${this.y.toFixed(2)} ${get(this, 'series.tooltipOptions.valueSuffix', '')}` : '';
  };

/**
 * Takes in the Fl or RL Data Rate chart point data and calls the SLA bound formatter by looking up the lower and upper
 * bounds in the terminal history
 */
export const getDataRateChartTooltipFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point> = function () {
  // The id for the chart, which is confiured in frontend/src/components/fleet/FullTailView/charts/ChartSeriesItems.ts
  const seriesId = this?.series?.userOptions?.id;

  // Calculate the sourceTime (unix) by dividing the current point's x value, which is in ms since epoch and
  // get the TerminalHistory entry and the point
  const sourceTimeAtX = this.x / 1000;
  const customOptions = this?.series?.options?.custom || {};
  const primaryTerminalHistory = customOptions.getPrimaryTerminalHistory?.();
  const secondaryTerminalHistory = customOptions.getSecondaryTerminalHistory?.();
  const terminalHistory =
    (seriesId?.includes(SECONDARY_HISTORY_SUFFIX) ? secondaryTerminalHistory : primaryTerminalHistory) || {};

  // Helper function to grab the correct SLA for the relevant seriesId
  const getSlaFromHistory = (linkType: string, terminalHistory: TerminalHistory) => {
    const slaAtPoint: TerminalHistory = get(terminalHistory, sourceTimeAtX, {});
    const { flSlaLowerBoundBps, flSlaUpperBoundBps, rlSlaLowerBoundBps, rlSlaUpperBoundBps } = slaAtPoint;
    return linkType === 'fl'
      ? formatSlaForDisplay(flSlaLowerBoundBps, flSlaUpperBoundBps)
      : formatSlaForDisplay(rlSlaLowerBoundBps, rlSlaUpperBoundBps);
  };

  // Get the FlDataRate and RlDateRate chart series keys (the data rate series' key and sla series' key)
  const flChartSeriesMetadata = chartSeriesContent[ChartItemKeys.flDataRate];
  const rlChartSeriesMetadata = chartSeriesContent[ChartItemKeys.rlDataRate];

  const [flDataRateSeries, flSlaSeries] = flChartSeriesMetadata;
  const [rlDataRateSeries, rlSlaSeries] = rlChartSeriesMetadata;

  const { key: flDataRateSeriesKey } = flDataRateSeries;
  const { key: flSlaSeriesKey } = flSlaSeries;
  const { key: rlDataRateSeriesKey } = rlDataRateSeries;
  const { key: rlSlaSeriesKey } = rlSlaSeries;

  if (seriesId && (seriesId.includes(flDataRateSeriesKey) || seriesId.includes(rlDataRateSeriesKey))) {
    // If the series is either the Fl Data Rate series or RL Data Rate series, format the y value to bps
    return !isNil(this.y) ? bpsToUnitString(this.y) : '';
  } else if (seriesId && seriesId.includes(flSlaSeriesKey)) {
    // If the series is the FL SLA series, get the lower and upper bounds from the terminal history points and
    // format the SLA with with the lower and upper bounds
    return getSlaFromHistory('fl', terminalHistory);
  } else if (seriesId && seriesId.includes(rlSlaSeriesKey)) {
    // If the series is the RL SLA series, get the lower and upper bounds from the terminal history points and
    // format the SLA with with the lower and upper bounds
    return getSlaFromHistory('rl', terminalHistory);
  } else {
    return `${get(this, 'y', EMPTY_FIELD_STRING_PLACEHOLDER)}`;
  }
};

export const addCustomMetadataGetterToSeries = (options: any, metadataLabelDivider = '') => {
  options.plotOptions = {
    ...options.plotOptions,
    line: {
      ...get(options, 'plotOptions.line'),
      custom: {
        ...get(options, 'plotOptions.line.custom'),
        metadataLabelDivider,
        metaData: [serviceRegionHeaderMetadataGetter],
      },
    },
  };
  return options;
};

/*
  Gets the service region at the x val
 */
export function serviceRegionHeaderMetadataGetter(): string {
  // @ts-ignore
  const { points, primaryTerminalHistory, secondaryTerminalHistory, selectedTerminalTabIndex } = this;

  // Select the appropriate terminal history to use
  const seriesId = get(points, 'series.userOptions.id', '');
  const terminalHistory =
    (seriesId.includes(SECONDARY_HISTORY_SUFFIX) ? secondaryTerminalHistory : primaryTerminalHistory) || {};

  // Determine whether the chart is Ku + Ka and what prefix to use for the service region
  const isKuPlusKa = Object.keys(secondaryTerminalHistory || {}).length > 0;
  const isOverviewTab = (selectedTerminalTabIndex || 0) === 0;
  const kuPlusKaPrefix = seriesId.includes(SECONDARY_HISTORY_SUFFIX) ? 'Ku: ' : 'Ka: ';

  // Construct service region name label
  const labelPrefix = isKuPlusKa && isOverviewTab ? kuPlusKaPrefix : '';
  const serviceRegionName = get(terminalHistory, `[${get(points, 'x', 0) / 1000}].serviceRegionName`, null);
  return serviceRegionName ? labelPrefix + serviceRegionName : '';
}
